import { SyntheticEvent, useMemo, useRef, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Divider, Drawer, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CheckboxGroupOptions, CheckboxGroupVariants } from '@sage/ui-components';

import AssistanceLevelDrawer from './AssistanceLevelDrawer';
import { ObjectTypes, useAnalytics } from 'src/AnalyticsContext';
import DrawerBody from 'src/components/drawers/DrawerBody';
import DrawerFooter from 'src/components/drawers/DrawerFooter';
import type { DrawerComponentProps } from 'src/components/drawers/DrawerManager';
import DrawerTitle from 'src/components/drawers/DrawerTitle';
import { isSkilledNursingUnit } from 'src/constants/CareTypes';
import { Drawers } from 'src/constants/Drawers';
import { ResolutionTypes } from 'src/constants/ResolutionTypes';
import {
    IdToAssistanceLevelMap,
    IdToSkilledNursingAdlOptionMap,
    SectionGgAdls,
} from 'src/constants/SectionGgResolutionTypes';
import useCareTypeFeatures from 'src/hooks/useCareTypeFeatures';
import { Resident, Task } from 'src/store/sageAdminApi';
import { AssistanceLevel } from 'src/store/sageCareServiceApi';

export interface SelectSectionGgResolutionDrawerProps extends DrawerComponentProps {
    analyticsPrefix?: string;
    hideAccidentallyPressed?: boolean;
    hideFoundDevice?: boolean;
    hideRoutineCheck?: boolean;
    resident?: Resident;
    residentId?: string;
    task?: Task;
}

const DrawerName = 'Select Section GG Resolution Drawer';

function SelectSectionGgResolutionDrawer(props: SelectSectionGgResolutionDrawerProps) {
    const {
        analyticsPrefix,
        hasNextDrawer = false,
        onNext,
        resident,
        title = 'Select all care needs',
        hideAccidentallyPressed = false,
        hideFoundDevice = false,
        hideRoutineCheck = false,
        task,
    } = props;

    const { spacing, palette, zIndex } = useTheme();
    const { trackEvent } = useAnalytics();

    const hasResident = !!resident || (task?.unit?.currentOccupants ?? []).length > 0;
    const residentUnit = task?.unit ?? resident?.unit;
    const isSkilledNursing = isSkilledNursingUnit(residentUnit);

    const { getFeatures } = useCareTypeFeatures();
    const isObservationsEnabledForCareType = getFeatures(residentUnit?.careType).observations
        .isEnabled;

    const shouldShowObservationsDrawer = useMemo(
        () => isObservationsEnabledForCareType && hasResident,
        [isObservationsEnabledForCareType, hasResident]
    );

    const [selectedOutcomes, setSelectedOutcomes] = useState<
        Record<string, { assistanceLevel?: AssistanceLevel; helperLevel?: string }>
    >({});

    const selectedOutcomeCount = Object.keys(selectedOutcomes).length;

    const selectedOutcomeOptions = useMemo(
        () => Object.fromEntries(Object.keys(selectedOutcomes).map((id) => [id, true])),
        [selectedOutcomes]
    );

    const [selectedAdlId, setSelectedAdlId] = useState<string | undefined>();

    const pendingLoaSelectionRef = useRef<
        { adlId: string; level: AssistanceLevel; helperLevel?: string } | undefined
    >(undefined);

    const filteredSectionGgAdls = useMemo(() => {
        return SectionGgAdls.map((category) => ({
            ...category,
            subTypes: category.subTypes?.filter((adl) => {
                if (hideAccidentallyPressed && adl.id === ResolutionTypes.AccidentallyPressed.id) {
                    return false;
                }
                if (hideFoundDevice && adl.id === ResolutionTypes.FoundDevice.id) {
                    return false;
                }
                if (hideRoutineCheck && adl.id === ResolutionTypes.RoutineCheck.id) {
                    return false;
                }
                return true;
            }),
        }));
    }, [hideAccidentallyPressed, hideFoundDevice, hideRoutineCheck]);

    const handleSelectAdl = (adlId: string, isChecked: boolean) => {
        trackEvent('Resolution Type Clicked', {
            resolutionId: adlId,
            isChecked,
            objectType: ObjectTypes.Checkbox,
            context: DrawerName,
        });

        const adlOption = IdToSkilledNursingAdlOptionMap.get(adlId);
        const isOtherHelpAdl = adlOption?.category?.id === 'OTHER_HELP';
        const isEmergencyAdl = adlOption?.category?.id === 'EMERGENCY';
        const isRequestAdl = adlOption?.category?.id === 'NON_EMERGENCY_REQUEST';

        if (isChecked) {
            if (isOtherHelpAdl || isEmergencyAdl || isRequestAdl) {
                setSelectedOutcomes((prev) => ({ ...prev, [adlId]: {} }));
            } else {
                setSelectedAdlId(adlId);
            }
        } else {
            setSelectedOutcomes((prev) => {
                const next = { ...prev };
                delete next[adlId];
                return next;
            });
        }
    };

    const handleSelectLevelOfAssistance = (level: AssistanceLevel, helperLevel?: string) => {
        if (!selectedAdlId) return;
        pendingLoaSelectionRef.current = { adlId: selectedAdlId, level, helperLevel };
        setSelectedAdlId(undefined);
    };

    const handleAssistanceLevelDrawerExited = () => {
        const pending = pendingLoaSelectionRef.current;
        if (!pending) return;
        pendingLoaSelectionRef.current = undefined;
        setSelectedOutcomes((prev) => ({
            ...prev,
            [pending.adlId]: {
                assistanceLevel: pending.level,
                ...(pending.level === 'DEPENDENT' && pending.helperLevel
                    ? { helperLevel: pending.helperLevel }
                    : {}),
            },
        }));
    };

    const handleCloseLevelOfAssistanceDrawer = (event?: InputEvent | {}, reason?: string) => {
        trackEvent('Assistance Level Drawer Closed', { reason, context: DrawerName });

        if (selectedAdlId) {
            setSelectedOutcomes((prev) => {
                const next = { ...prev };
                delete next[selectedAdlId];
                return next;
            });
        }
        setSelectedAdlId(undefined);
    };

    const handleClearAll = () => {
        trackEvent('Clear All Clicked', {
            context: DrawerName,
            numResolutionsCleared: selectedOutcomeCount,
        });

        setSelectedOutcomes({});
    };

    const handleSelectButton = (e: SyntheticEvent) => {
        e.preventDefault();

        trackEvent('Select Button Clicked', {
            objectType: ObjectTypes.Button,
            context: DrawerName,
            taskId: task?.id,
            numResolutionsSelected: selectedOutcomeCount,
            isSkilledNursing,
        });

        const careOutcomes = Object.keys(selectedOutcomes).map((careProvided) => ({
            outcome: 'COMPLETED' as const,
            careProvided,
            ...(selectedOutcomes[careProvided] ?? {}),
        }));

        onNext(
            {
                analyticsPrefix,
                careOutcomes,
                alertOutcomeType: 'RESIDENT_INTERACTION',
                isEmergency: task?.severity === 'EMERGENCY' || false,
                requireSelection: false,
            },
            hasNextDrawer
                ? undefined
                : shouldShowObservationsDrawer
                ? {
                      type: Drawers.AddObservations,
                      fullHeight: true,
                  }
                : { type: Drawers.ResolveTask }
        );
    };

    return (
        <>
            <DrawerTitle sx={{ p: 0, gap: 2 }}>
                <Box sx={{ px: 2.5, pt: 9 }}>
                    <Typography variant="h2" sx={{ fontWeight: 'bold', textAlign: 'left' }}>
                        {title}
                    </Typography>
                </Box>
                <Divider />
            </DrawerTitle>

            <DrawerBody sx={{ p: 0 }}>
                <Stack divider={<Divider />} sx={{ pt: 2.5 }}>
                    {filteredSectionGgAdls.map((resolutionCategory, index) => (
                        <Stack
                            key={resolutionCategory.displayName || `no-category-${index}`}
                            sx={{ pb: 2.5 }}
                        >
                            {resolutionCategory.displayName && (
                                <Box
                                    sx={{
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 1,
                                        bgcolor: 'grey.50',
                                        py: 1.5,
                                        px: 2.5,
                                        mt: 1.5,
                                    }}
                                >
                                    <Typography
                                        variant="subtitle1"
                                        component="h2"
                                        sx={{ textTransform: 'uppercase' }}
                                    >
                                        {resolutionCategory.displayName}
                                    </Typography>
                                </Box>
                            )}
                            <Box sx={{ px: 2.5 }}>
                                <CheckboxGroupOptions
                                    options={(resolutionCategory.subTypes ?? []).map(
                                        (resolutionType) => ({
                                            value: resolutionType.id,
                                            highlighted:
                                                resolutionType.id === ResolutionTypes.Medtech.id,
                                            label: (
                                                <Stack
                                                    direction="row"
                                                    sx={{ alignItems: 'center' }}
                                                >
                                                    <Stack
                                                        direction="row"
                                                        sx={{ gap: 1.5, alignItems: 'center' }}
                                                    >
                                                        {resolutionType.icon && (
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    width: spacing(5),
                                                                    height: spacing(5),
                                                                }}
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon={resolutionType.icon}
                                                                    color={
                                                                        resolutionType.category
                                                                            ?.id === 'EMERGENCY'
                                                                            ? palette.error.main
                                                                            : palette.common.black
                                                                    }
                                                                    size="2x"
                                                                    fixedWidth
                                                                />
                                                            </Box>
                                                        )}

                                                        <Stack sx={{ flexGrow: 1 }}>
                                                            <Typography
                                                                variant="body1"
                                                                sx={{
                                                                    fontWeight: 'bold',
                                                                    color: 'grey.950',
                                                                    flex: '1',
                                                                }}
                                                            >
                                                                {resolutionType.displayName}
                                                            </Typography>
                                                            {selectedOutcomes[resolutionType.id]
                                                                ?.assistanceLevel && (
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{ color: 'grey.500' }}
                                                                >
                                                                    {IdToAssistanceLevelMap.get(
                                                                        selectedOutcomes[
                                                                            resolutionType.id
                                                                        ].assistanceLevel!
                                                                    )?.displayName ??
                                                                        selectedOutcomes[
                                                                            resolutionType.id
                                                                        ].assistanceLevel}
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                    </Stack>
                                                </Stack>
                                            ),
                                        })
                                    )}
                                    selectedOptions={selectedOutcomeOptions}
                                    onSelectOption={handleSelectAdl}
                                    variant={CheckboxGroupVariants.Outlined}
                                    formGroupProps={{
                                        'aria-label': `Select ${resolutionCategory.displayName}`,
                                    }}
                                />
                            </Box>
                        </Stack>
                    ))}
                </Stack>
            </DrawerBody>

            <DrawerFooter showShadow sx={{ bgcolor: 'common.white' }}>
                <Stack direction="row" sx={{ gap: 1.5, alignItems: 'center' }}>
                    <Button
                        variant="text"
                        color="primary"
                        onClick={handleClearAll}
                        disableFocusRipple
                        disableElevation
                        disabled={selectedOutcomeCount === 0}
                    >
                        <Typography variant="body1" sx={{ whiteSpace: 'nowrap' }}>
                            Clear All
                        </Typography>
                    </Button>
                    <Button
                        fullWidth
                        color="primary"
                        type="submit"
                        variant="contained"
                        onClick={handleSelectButton}
                        disableFocusRipple
                        disableElevation
                        disabled={selectedOutcomeCount === 0}
                    >
                        <Typography variant="body1" sx={{ whiteSpace: 'nowrap' }}>
                            {selectedOutcomeCount === 0
                                ? 'Select'
                                : 'Select (' + selectedOutcomeCount + ')'}
                        </Typography>
                    </Button>
                </Stack>
            </DrawerFooter>

            <Drawer
                anchor="bottom"
                open={!!selectedAdlId}
                onClose={handleCloseLevelOfAssistanceDrawer}
                onTransitionExited={handleAssistanceLevelDrawerExited}
                slotProps={{
                    paper: { sx: { borderRadius: `${spacing(2.5)} ${spacing(2.5)} 0 0` } },
                }}
                sx={{ zIndex: zIndex.drawer }}
            >
                <AssistanceLevelDrawer
                    selectedAdlId={selectedAdlId!}
                    onSelect={handleSelectLevelOfAssistance}
                    onClose={() => handleCloseLevelOfAssistanceDrawer(undefined, 'closeIcon')}
                />
            </Drawer>
        </>
    );
}

export default SelectSectionGgResolutionDrawer;
